/**
 * Maps a pointer event's client coords to the Rust overlay's scene coordinates.
 *
 * The overlay canvas has an intrinsic buffer size (`canvasWidth`×`canvasHeight`, the render-scaled
 * scene) but is displayed CSS-scaled to `rect.width`×`rect.height`. Hit-testing runs against the
 * same scene the engine rendered, so we map the on-screen click back into buffer/scene px.
 */
export function overlayPointToScene(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  canvasWidth: number,
  canvasHeight: number,
): { x: number; y: number } {
  return {
    x: (clientX - rect.left) * (canvasWidth / rect.width),
    y: (clientY - rect.top) * (canvasHeight / rect.height),
  };
}

export interface SceneRect {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Plain rotation (deg) for boxes whose rotation lives in rect.rotation (objects/images), not an affine. */
  rotation?: number;
  /** Box draw-time affine (rotated/flipped text) — so the selection/hover frame rotates with it. */
  transform?: number[];
  /** Designer-locked: still indexed so a panel selection can be framed, but never a pointer target. */
  locked?: boolean;
  /** Glyph ink bbox ∪ rect of overflowing point text — region lifts must copy it; chrome/hit keep rect. */
  ink?: { x: number; y: number; width: number; height: number };
}

/**
 * Indexes drawable id -> its (scaled) scene rect + transform, for the selection layer. Non-interactive
 * shapes (zones, the selection box itself) are excluded so they can't be selected — matches the Rust
 * `hit_test`. Interactive shapes (empty image-frame placeholders) ARE indexed so they hover/select.
 */
export function indexDrawableRects(sceneJson: string): Map<string, SceneRect> {
  const map = new Map<string, SceneRect>();
  try {
    type Indexed = {
      id: string;
      type: string;
      rect?: SceneRect;
      transform?: number[];
      interactive?: boolean;
      locked?: boolean;
      inkRect?: { x: number; y: number; width: number; height: number };
      children?: Indexed[];
    };
    const scene = JSON.parse(sceneJson) as { drawables?: Indexed[] };
    // A PSD group node is never a target itself (Rust `hit_test`): its leaves are indexed in draw order.
    const walk = (list: readonly Indexed[]): void => {
      for (const d of list) {
        if (d.type === 'group') walk(d.children ?? []);
        else if ((d.type !== 'shape' || d.interactive) && d.rect) {
          map.set(d.id, { ...d.rect, transform: d.transform, locked: d.locked, ink: d.inkRect });
        }
      }
    };

    walk(scene.drawables ?? []);
  } catch {
    // malformed scene -> empty index (nothing selectable)
  }

  return map;
}

type SceneNode = { id: string; type: string; rect?: SceneRect; transform?: number[]; locked?: boolean; children?: SceneNode[] };

/**
 * The topmost unlocked text drawable whose frame, grown by `slop` scene px, holds the point — whatever is drawn above
 * it. Hidden boxes never reach the scene; a locked group locks its subtree, as in the Rust `hit_test`.
 */
export function topmostTextAt(sceneJson: string, x: number, y: number, slop: number): string | undefined {
  let found: string | undefined;
  const walk = (list: readonly SceneNode[], locked: boolean): void => {
    for (const d of list) {
      const off = locked || !!d.locked;
      if (d.type === 'group') walk(d.children ?? [], off);
      else if (d.type === 'text' && !off && d.rect && textFrameHolds(d.rect, d.transform, x, y, slop)) found = d.id;
    }
  };

  try {
    walk((JSON.parse(sceneJson) as { drawables?: SceneNode[] }).drawables ?? [], false);
  } catch {
    return undefined;
  }

  return found;
}

// The frame lives in text space; the draw affine maps it to the scene, so the point goes back through its inverse.
function textFrameHolds(r: SceneRect, t: number[] | undefined, x: number, y: number, slop: number): boolean {
  let [lx, ly, s] = [x, y, slop];
  if (t?.length === 6) {
    const [a, b, c, d, e, f] = t;
    const det = a * d - b * c;
    if (Math.abs(det) > 1e-9) {
      lx = (d * (x - e) - c * (y - f)) / det;
      ly = (-b * (x - e) + a * (y - f)) / det;
      s = slop / Math.sqrt(Math.abs(det));
    }
  }

  return lx >= r.x - s && lx <= r.x + r.width + s && ly >= r.y - s && ly <= r.y + r.height + s;
}

/**
 * Fallback for a click the engine reported as a miss: the topmost UNLOCKED drawable whose bounds cover the
 * point. Lets a designer grab a decor by its empty middle once the locked background stops answering clicks.
 * Map order mirrors scene order, so the last match is the topmost.
 */
export function topmostRectAt(rects: ReadonlyMap<string, SceneRect>, x: number, y: number): string | undefined {
  let found: string | undefined;
  for (const [id, r] of rects) {
    if (r.locked) continue;
    if (boundsContain(r, x, y)) found = id;
  }

  return found;
}

/**
 * Whether a locked drawable covers the point — i.e. an engine miss there means the lock ate the click.
 *
 * The synthetic page fill (`page-fill-<i>`, album-document `pageFillBoxes`) is locked and covers the whole
 * page, so it would make every miss look eaten and hand bare page space to the bbox fallback. It is bare
 * paper, not a lock over content: it never counts.
 */
export function lockedRectCovers(rects: ReadonlyMap<string, SceneRect>, x: number, y: number): boolean {
  for (const [id, r] of rects) {
    if (r.locked && !id.startsWith('page-fill-') && boundsContain(r, x, y)) return true;
  }

  return false;
}

function boundsContain(r: SceneRect, x: number, y: number): boolean {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of boxCorners(r)) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }

  return x >= minX && x <= maxX && y >= minY && y <= maxY;
}

export interface LiftSourceRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/** Region-lift view of a rect: ink bounds (already ∪ rect) replace the frame; transform is kept for corners. */
export function inkExpanded(r: SceneRect): SceneRect {
  return r.ink ? { ...r, x: r.ink.x, y: r.ink.y, width: r.ink.width, height: r.ink.height } : r;
}

// The visible pixels of a rotated/sheared box overflow its axis-aligned rect, so the lift region must follow
// the same transform the renderer applies. Plain rotation is rotation-about-center (matches the selection frame),
// so its AABB is exact. The per-box affine convention is less certain, so we union it with the upright rect to
// guarantee coverage (a transient over-copy is safer than clipping; the clean WASM lift refines it a frame later).
function boxCorners(r: SceneRect): Array<{ x: number; y: number }> {
  const upright = [
    { x: r.x, y: r.y },
    { x: r.x + r.width, y: r.y },
    { x: r.x + r.width, y: r.y + r.height },
    { x: r.x, y: r.y + r.height },
  ];
  if (r.transform && r.transform.length === 6) {
    const [a, b, c, d, e, f] = r.transform;
    const affine = upright.map(p => ({ x: a * p.x + c * p.y + e, y: b * p.x + d * p.y + f }));
    return [...upright, ...affine];
  }

  if (r.rotation) {
    const cx = r.x + r.width / 2;
    const cy = r.y + r.height / 2;
    const rad = (r.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return upright.map(p => {
      const dx = p.x - cx;
      const dy = p.y - cy;
      return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
    });
  }

  return upright;
}

/**
 * Integer source rectangle (clamped to the canvas) covering a drawable's visible pixels, for an instant
 * Canvas2D `drawImage` lift off the already-rendered overlay — no WASM rasterise. Accounts for rotation /
 * affine skew and pads for anti-aliasing/effect slack; the clean WASM lift refines it a frame later.
 */
export function liftSourceRect(r: SceneRect, canvasW: number, canvasH: number, pad = 2): LiftSourceRect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of boxCorners(r)) {
    const x = Math.round(p.x * 1e4) / 1e4;
    const y = Math.round(p.y * 1e4) / 1e4;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }

  const sx = Math.max(0, Math.floor(minX - pad));
  const sy = Math.max(0, Math.floor(minY - pad));
  const sw = Math.max(0, Math.min(canvasW, Math.ceil(maxX + pad)) - sx);
  const sh = Math.max(0, Math.min(canvasH, Math.ceil(maxY + pad)) - sy);

  return { sx, sy, sw, sh };
}
